/**
 * Export Utilities for Transactions
 * Provides functionality to export transaction data to Excel format
 */

import { type Transaction } from "@/types/transaction";
import { formatDateDDMMYYYY, formatCurrency } from "@/lib/utils";

/**
 * Export transactions to Excel (CSV format)
 * @param transactions - Array of transactions to export
 * @param filename - Optional filename (defaults to transactions-{date}.csv)
 */
export function exportTransactionsToExcel(
  transactions: Transaction[],
  filename?: string
): void {
  if (transactions.length === 0) {
    throw new Error("No transactions to export");
  }

  // Define columns to export
  const columns = [
    { key: "referenceNumber", label: "Reference Number" },
    { key: "transactionDate", label: "Transaction Date" },
    { key: "dueDate", label: "Due Date" },
    { key: "customerName", label: "Customer Name" },
    { key: "transactionType", label: "Type" },
    { key: "amount", label: "Gross Amount" },
    { key: "outstandingAmount", label: "Outstanding Amount" },
    { key: "status", label: "Status" },
    { key: "description", label: "Description" },
    { key: "accountCode", label: "Account Code" },
    { key: "netAmount", label: "Net Amount" },
    { key: "vatAmount", label: "VAT Amount" },
    { key: "customerReference", label: "Customer Reference" },
    { key: "periodNumber", label: "Period Number" },
    { key: "year", label: "Year" },
    { key: "onDispute", label: "On Dispute" },
    { key: "subLedger", label: "Sub Ledger" },
    { key: "orderNumber", label: "Order Number" },
    { key: "createdBy", label: "Created By" },
    { key: "createdAt", label: "Created At" },
    { key: "modifiedBy", label: "Modified By" },
    { key: "modifiedAt", label: "Modified At" },
  ];

  // Create CSV header
  const header = columns.map((col) => `"${col.label}"`).join(",");

  // Create CSV rows
  const rows = transactions.map((transaction) => {
    return columns
      .map((col) => {
        const key = col.key as keyof Transaction;
        let value = transaction[key];

        // Format specific fields
        if (key === "transactionDate" || key === "dueDate") {
          value = value ? formatDateDDMMYYYY(value as string) : "";
        } else if (key === "createdAt" || key === "modifiedAt") {
          value = value ? new Date(value as string).toLocaleString() : "";
        } else if (key === "amount" || key === "outstandingAmount" || key === "netAmount" || key === "vatAmount") {
          value = value !== undefined ? formatCurrency(value as number, transaction.currency) : "";
        } else if (key === "transactionType") {
          value = formatTransactionType(value as string);
        } else if (key === "status") {
          value = formatStatus(value as string);
        } else if (key === "onDispute") {
          value = value ? "Yes" : "No";
        } else if (value === undefined || value === null) {
          value = "";
        }

        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  // Combine header and rows
  const csv = [header, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    filename || `transactions-${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Format transaction type for display
 */
function formatTransactionType(type: string): string {
  const typeMap: Record<string, string> = {
    invoice: "Invoice",
    credit_note: "Credit Note",
    receipt: "Receipt",
    debit_adjustment: "Debit Adjustment",
    credit_adjustment: "Credit Adjustment",
  };
  return typeMap[type] || type;
}

/**
 * Format status for display
 */
function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    pending: "Pending",
    completed: "Completed",
    cancelled: "Cancelled",
    void: "Void",
    draft: "Draft",
    overdue: "Overdue",
    partially_paid: "Partially Paid",
  };
  return statusMap[status] || status;
}

/**
 * Calculate totals for selected transactions
 */
export interface TransactionTotals {
  count: number;
  totalAmount: number;
  totalOutstanding: number;
  currency: string;
}

export function calculateTransactionTotals(
  transactions: Transaction[]
): TransactionTotals {
  if (transactions.length === 0) {
    return {
      count: 0,
      totalAmount: 0,
      totalOutstanding: 0,
      currency: "GBP",
    };
  }

  const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);
  const totalOutstanding = transactions.reduce(
    (sum, t) => sum + (t.outstandingAmount || 0),
    0
  );

  // Use the currency from the first transaction
  const currency = transactions[0].currency;

  return {
    count: transactions.length,
    totalAmount,
    totalOutstanding,
    currency,
  };
}
